import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { notify } from './utils/helpers';
import { dateCellSelection, slotWidth, getCellAtX, pointInBox } from './utils/selection';
import Selection, { getBoundsForNode } from './Selection';

const DisplayCells = ({
  selectable,
  slots,
  onSelectStart,
  onSelectEnd,
  onSelectSlot,
  container // Added container prop
}) => {
  const [selecting, setSelecting] = useState(false);
  const [startIdx, setStartIdx] = useState(-1);
  const [endIdx, setEndIdx] = useState(-1);
  const [initial, setInitial] = useState({});
  const selectorRef = useRef(null);
  const nodeRef = useRef(null);

  useEffect(() => {
    if (selectable) {
      setupSelectable();
    } else {
      teardownSelectable();
    }

    return () => {
      teardownSelectable();
    };
  }, [selectable, slots, onSelectStart, onSelectEnd, onSelectSlot, container]);

  const setupSelectable = () => {
    if (!nodeRef.current) return;
    
    const selector = new Selection(container);
    selectorRef.current = selector;

    selector.on('selecting', (box) => {
      let newStartIdx = -1;
      let newEndIdx = -1;

      if (!selecting) {
        notify(onSelectStart, [box]);
        setInitial({ x: box.x, y: box.y });
      }

      if (selector.isSelected(nodeRef.current)) {
        const nodeBox = getBoundsForNode(nodeRef.current);
        const selectionResult = dateCellSelection(initial, nodeBox, box, slots);
        newStartIdx = selectionResult.startIdx;
        newEndIdx = selectionResult.endIdx;
      }

      setSelecting(true);
      setStartIdx(newStartIdx);
      setEndIdx(newEndIdx);
    });

    selector.on('click', (point) => {
      const rowBox = getBoundsForNode(nodeRef.current);

      if (pointInBox(rowBox, point)) {
        const width = slotWidth(rowBox, slots);
        const currentCell = getCellAtX(rowBox, point.x, width);

        selectSlot({
          startIdx: currentCell,
          endIdx: currentCell,
        });
      }

      setInitial({});
      setSelecting(false);
    });

    selector.on('select', () => {
      selectSlot({ startIdx, endIdx });
      setInitial({});
      setSelecting(false);
      notify(onSelectEnd, [{ startIdx, endIdx }]);
    });
  };

  const teardownSelectable = () => {
    if (!selectorRef.current) return;
    selectorRef.current.teardown();
    selectorRef.current = null;
  };

  const selectSlot = ({ startIdx, endIdx }) => {
    onSelectSlot && onSelectSlot({
      start: startIdx,
      end: endIdx,
    });
  };

  const children = [];

  for (let i = 0; i < slots; i++) {
    children.push(
      <div
        key={`bg_${i}`}
        className={cn('rbc-day-bg', {
          'rbc-selected-cell': selecting && i >= startIdx && i <= endIdx,
        })}
      />
    );
  }

  return (
    <div className="rbc-row-bg" ref={nodeRef}>
      {children}
    </div>
  );
};

DisplayCells.propTypes = {
  selectable: PropTypes.bool,
  slots: PropTypes.number,
  onSelectStart: PropTypes.func,
  onSelectEnd: PropTypes.func,
  onSelectSlot: PropTypes.func,
  container: PropTypes.any, // Added container prop type
};

export default DisplayCells;